import { RegisterLifterCompetitionTypeSelect } from './register-result-competition-type';
import { RegisterLifterAgeCategorySelect } from './register-result-age-category';
import { CompetitionTeamSelect } from '~/pages/competition/teams/components/competition-team-select';
import { GroupSelect } from '~/pages/competition/groups/components/group-select';
import { ResultColumn } from './result-columns';
import { useResultForm } from './use-result-form';
import { Button } from '~/components/Button/Button';
import { LifterCell } from './lifter-cell';
import { Input } from '~/components/Input';
import styles from './result-grid.module.css';
import { CSSProperties } from 'react';
import classNames from 'classnames';

interface Props {
  lifterId: number;
  /** Display name shown in the identity column. */
  name: string;
  /** Secondary line under the name, e.g. "license · club". Omitted for ad-hoc added lifters. */
  meta?: string;
  /** Pre-selected (but still editable) competition team, when the lifter's team is already known. */
  teamId?: number;
  competitionId: number;
  isTeamComp: boolean;
  columns: ResultColumn[];
  template: string;
  /** Render the row standalone (per-field labels instead of relying on a shared column header). */
  labeled?: boolean;
  onFinish?: () => void;
}

export function ResultRow({
  lifterId,
  name,
  meta,
  teamId,
  competitionId,
  isTeamComp,
  columns,
  template,
  labeled = false,
  onFinish,
}: Props) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState,
    onSubmit,
    isPending,
    isBenchpressCompetitionType,
    isU1113Category,
    setSelectedAgeLabel,
  } = useResultForm({ lifterId, competitionId, isTeamComp, teamId, onFinish });

  const errors = formState.errors as Record<string, { message?: string } | undefined>;
  const errorLabels = columns.filter(c => c.key !== 'lifter' && c.key !== 'save' && errors[c.key]).map(c => c.label);

  function renderField(col: ResultColumn) {
    switch (col.key) {
      case 'competition_type':
        return (
          <RegisterLifterCompetitionTypeSelect
            {...register('competition_type')}
            onChange={event => setValue('competition_type', event.target.value)}
            formState={formState}
            defaultValue={undefined}
            aria-label="Tävlingstyp"
            placeholder="Tävlingstyp"
          />
        );
      case 'age_category':
        return (
          <RegisterLifterAgeCategorySelect
            {...register('age_category')}
            onChange={event => {
              setValue('age_category', Number(event.target.value));
              const selectedOption = event.target.options[event.target.selectedIndex];
              setSelectedAgeLabel(selectedOption?.text);
            }}
            formState={formState}
            defaultValue={undefined}
            aria-label="Ålderskategori"
            placeholder="Ålderskategori"
          />
        );
      case 'body_weight':
        return (
          <Input.Field
            type="number"
            step="0.01"
            min={0}
            aria-label="Kroppsvikt"
            formState={formState}
            {...register('body_weight', { valueAsNumber: true })}
          />
        );
      case 'squat':
        return (
          <Input.Field
            type="number"
            step="0.05"
            min={0}
            aria-label="Knäböj"
            formState={formState}
            {...register('squat', { valueAsNumber: true, disabled: isBenchpressCompetitionType })}
          />
        );
      case 'benchpress':
        return (
          <Input.Field
            type="number"
            step="0.05"
            min={0}
            aria-label="Bänkpress"
            formState={formState}
            {...register('benchpress', { valueAsNumber: true })}
          />
        );
      case 'deadlift':
        return (
          <Input.Field
            type="number"
            step="0.05"
            min={0}
            aria-label="Marklyft"
            formState={formState}
            {...register('deadlift', { valueAsNumber: true, disabled: isBenchpressCompetitionType })}
          />
        );
      case 'group':
        return <GroupSelect {...register('group')} placeholder="-" formState={formState} aria-label="Grupp" />;
      case 'team':
        return (
          <CompetitionTeamSelect
            competitionId={competitionId}
            {...register('team')}
            value={watch('team') ?? undefined}
            formState={formState}
          />
        );
      case 'lot':
        return <Input.Field type="number" aria-label="Lott" formState={formState} {...register('lot')} />;
      case 'rank':
        return <Input.Field type="number" aria-label="Placering" formState={formState} {...register('rank')} />;
      case 'is_coef_group':
        return <Input.Checkbox {...register('is_coef_group')} formState={formState} />;
      case 'disqualified':
        return <Input.Checkbox {...register('disqualified')} formState={formState} />;
      default:
        return null;
    }
  }

  return (
    <form
      className={classNames(styles.row, labeled && styles.labeled)}
      style={{ '--cols': template } as CSSProperties}
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      {columns.map(col => {
        if (col.key === 'lifter') {
          return <LifterCell key={col.key} name={name} meta={meta} label={labeled ? 'Lyftare' : undefined} />;
        }

        if (col.key === 'save') {
          return (
            <div key={col.key} className={classNames(styles.cell, styles.saveCell)}>
              <Button type="submit" isLoading={isPending} block>
                Spara
              </Button>
            </div>
          );
        }

        return (
          <div key={col.key} className={classNames(styles.cell, col.align === 'center' && styles.center)}>
            <span className={styles.cellLabel}>{col.short ?? col.label}</span>
            {renderField(col)}
          </div>
        );
      })}

      {isU1113Category && (
        <div className={styles.extraRow}>
          <label className={styles.extraField}>
            <span className={styles.extraLabel}>Godkända lyft</span>
            <Input.Field
              type="number"
              aria-label="Godkända lyft"
              formState={formState}
              {...register('good_lifts', { valueAsNumber: true })}
            />
          </label>
          <label className={styles.extraField}>
            <span className={styles.extraLabel}>Varav godkända bänkpressar</span>
            <Input.Field
              type="number"
              aria-label="Varav godkända bänkpressar"
              formState={formState}
              {...register('good_lifts_bp', { valueAsNumber: true })}
            />
          </label>
        </div>
      )}

      {errorLabels.length > 0 && <p className={styles.rowErrors}>Fyll i: {errorLabels.join(', ')}</p>}
    </form>
  );
}
